import { createSlice, PayloadAction, SerializedError } from '@reduxjs/toolkit'
import { LanguageCode, LanguageType } from '../i18n/i18n'
import { authenticateUser } from '../store/api/authenticate-user';

type BooleanAttribute = 'true' | 'false';

export interface NameAttributes {
  family_name: string,
  given_name: string,
}

export interface Session {
  accessToken: string
  idToken: string,
  refreshToken: string,
}

export interface Attributes extends NameAttributes {
  'custom:language'?: string,

  // custom attributes can be optional
  // and shouldalways be assumed to be string

  'custom:switch_to_email_flow': BooleanAttribute,
  'custom:is_registered': BooleanAttribute,
  'custom:user_profile_uuid'?: string,
  'custom:users_migration_src'? : string,
  sub?: string,
  email: string,
  email_verified: boolean,
}

export type Loading = 'idle' | 'pending' | 'succeeded' | 'failed';
export type Authentication = 'idle' | 'pending' | 'succeeded' | 'failed';
export type Lang = LanguageCode | '';
export type Origin = 'sdi' | '';

interface SearchParameters {
  redirect_uri: string,
  redirect_origin: string,
  logout_uri: string,
  authorization_code: string,
  state: string,
  client_id: string,
  lang: Lang,
  origin: Origin,
  macsxhost: string,
}

export interface AppState {
  loading: Loading,
  authentication: Authentication,
  currentRequestId: string,
  language: LanguageCode,
  title: string,
  error: SerializedError | null,
  redirect_app: string,
  session: Session,
  attributes: Attributes,
  searchParameters: SearchParameters,
}

const initialState: AppState = {
  loading: 'idle',
  authentication: 'pending',
  currentRequestId: '',
  language: 'en',
  title: '',
  error: null,
  redirect_app: '',
  session: {
    accessToken: '',
    idToken: '',
    refreshToken: '',
  },
  attributes: {
    "custom:switch_to_email_flow": "false",
    "custom:is_registered": "true",
    email: '',
    email_verified: false,
    family_name: '',
    given_name: '',
  },
  searchParameters: {
    logout_uri: '',
    redirect_uri: '',
    redirect_origin: '',
    authorization_code: '',
    state: '',
    client_id: '',
    lang: '',
    origin: '',
    macsxhost: ''
  }
}


interface CognitoSession {
  accessToken: {
    jwtToken: string,
  },
  idToken: {
    jwtToken: string,
  },
  refreshToken: {
    token: string,
  }
}




export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    languageChanged: (state, action: PayloadAction<LanguageCode>) => {
      state.language = action.payload;
    },
    userUnauthenticated: ( state, ) => {
      state.authentication = 'idle';
      state.attributes = {
        "custom:switch_to_email_flow": "false",
        "custom:is_registered": "true",
        email: '',
        email_verified: false,
        family_name: '',
        given_name: '',
      };
      state.session = {
        idToken: '',
        accessToken: '',
        refreshToken: '',
      }
      state.searchParameters = {
        logout_uri: '',
        authorization_code: '',
        redirect_uri: '',
        redirect_origin: '',
        state: '',
        client_id: '',
        lang: '',
        origin: '',
        macsxhost:''
      }
    },
    appRedirectionUpdated: (state, action: PayloadAction<string>) => {
      state.redirect_app = action.payload;
    },
    userAuthenticated: (state, action: PayloadAction<Attributes>) => {
      state.attributes = action.payload;
    },
    titleUpdated: (state, action: PayloadAction<string>) => {
      state.title = action.payload;
    },
    sessionUpdated: (state, action: PayloadAction<Session>) => {
      state.session = action.payload;
    },
    langUpdated: (state, action: PayloadAction<Lang>) => {
      state.searchParameters.lang = action.payload;
    },
    originCleared: ( state ) => {
      state.searchParameters.origin = '';
    },
    parametersUpdated: (state, action: PayloadAction<SearchParameters>) => {
      state.searchParameters = action.payload;
    }
  },
  extraReducers: ( builder ) => {
      builder.addCase( authenticateUser.pending, (state, action) => {
        if (state.loading === 'idle') {
          state.loading = 'pending'
          state.currentRequestId = action.meta.requestId
        } else {
        }
      })
      .addCase( authenticateUser.fulfilled, (state, action) => {

        const attributes = action.payload as Attributes;
        const { requestId } = action.meta

        if ( state.loading === 'pending' && state.currentRequestId === requestId) {

          state.currentRequestId = ''

          state.loading = 'idle';
          state.authentication = action.payload ? 'succeeded' : 'failed';
        }
      })
      .addCase( authenticateUser.rejected, ( state, action, ) => {
        const { requestId } = action.meta
        if ( state.loading === 'pending' && state.currentRequestId === requestId) {
          state.loading = 'idle'
          state.error = action.error
          state.currentRequestId = ''
        }
      })
  },
})

// Action creators are generated for each case reducer function
export const {
  languageChanged,
  sessionUpdated,
  appRedirectionUpdated,
  userAuthenticated,
  userUnauthenticated,
  parametersUpdated,
  titleUpdated,
  langUpdated,
  originCleared,
} = appSlice.actions

export default appSlice.reducer
